import {
  usePollMediaExportMutation,
  useStartMediaExportMutation,
} from "@/api/media-library";
import { useAuthStore } from "@/features/authentication";
import Button from "@components/ui/button";
import Icon from "@components/ui/icon";
import Tooltip from "@components/ui/tooltip";
import { UploadBtn } from "@editor/modules/media-lib/components/upload-btn";
import type {
  MediaLibPresetEnum,
  MediaPanelTabEnum,
} from "@editor/modules/media-lib/types";
import {
  downloadFromUrl,
  sendMediaLibErrorToast,
  sendMediaLibUploadedToast,
  sendMediaLibUploadingToast,
} from "@editor/modules/media-lib/utils";
import { IconDownload, IconX } from "@tabler/icons-react";
import { useMediaLibDialogCtx } from "@editor/modules/media-lib/ctx";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { MEDIA_DIALOG_HEADER } from "../constants";

const POLL_INTERVAL = 3000;

export const MediaDialogHeader = ({
  preset,
  setSelectedTab,
}: {
  preset: MediaLibPresetEnum;
  setSelectedTab: (tab: MediaPanelTabEnum) => void;
}) => {
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const { closeDialog } = useMediaLibDialogCtx();
  const { title, icon } = MEDIA_DIALOG_HEADER[preset];
  const abortRef = useRef<AbortController | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [startMediaExport] = useStartMediaExportMutation();
  const [pollMediaExport] = usePollMediaExportMutation();

  const pollUntilDone = async (
    jobId: string,
    signal: AbortSignal,
  ): Promise<string> => {
    while (!signal.aborted) {
      const result = await pollMediaExport({ jobId, signal }).unwrap();

      if (result.status === "done" && result.url) {
        return result.url;
      }

      if (result.status === "error") {
        throw new Error(result.error || "Export failed");
      }

      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, POLL_INTERVAL);
        signal.addEventListener(
          "abort",
          () => {
            clearTimeout(timer);
            reject(new DOMException("Aborted", "AbortError"));
          },
          { once: true },
        );
      });
    }

    throw new DOMException("Aborted", "AbortError");
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    const controller = new AbortController();
    abortRef.current = controller;

    const toastId = sendMediaLibUploadingToast({
      msg: "Preparing export...",
      onCancel: () => controller.abort(),
    });

    try {
      const { jobId } = await startMediaExport({
        signal: controller.signal,
      }).unwrap();

      const url = await pollUntilDone(jobId, controller.signal);

      toast.update(toastId, {
        render: "Downloading media...",
      });

      await downloadFromUrl(url, controller.signal);
      toast.dismiss(toastId);
      sendMediaLibUploadedToast({ msg: "Media exported" });
    } catch (err) {
      toast.dismiss(toastId);
      if ((err as Error).name === "AbortError") {
        abortRef.current = null;
        setIsExporting(false);
        return;
      }
      sendMediaLibErrorToast({
        msg: (err as Error).message || "Failed to export media",
        onRetry: handleExport,
      });
    }
    abortRef.current = null;
    setIsExporting(false);
  };

  return (
    <div className="tw-flex tw-h-[68px] tw-items-center tw-justify-between tw-rounded-t-2xl tw-p-6 tw-px-6 tw-py-5 -sm:tw-h-auto -sm:tw-px-3 -sm:tw-py-3 -sm:tw-gap-2 tw-min-w-0">
      <div className="tw-flex tw-w-full tw-min-w-0 tw-items-center tw-font-sans tw-text-base tw-font-medium tw-text-blue -sm:tw-w-auto -sm:tw-flex-1 tw-truncate">
        <Icon
          icon={icon}
          className="tw-mr-2 tw-size-5 tw-fill-primary tw-text-xl/none tw-text-primary tw-shrink-0"
        />
        <span className="tw-truncate">{title}</span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-2 tw-shrink-0">
        <UploadBtn setSelectedTab={setSelectedTab} />
        {isAdmin && (
          <Tooltip>
            <Tooltip.Trigger asChild>
              <Button
                variant="secondary"
                size="sm"
                className="tw-size-8 tw-p-0 -sm:tw-hidden"
                onClick={handleExport}
                isLoading={isExporting}
                title="Export all media"
              >
                <IconDownload className="tw-text-base/none !tw-fill-none" />
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content className="tw-z-[99999]">
              <Tooltip.Text>Export all media</Tooltip.Text>
            </Tooltip.Content>
          </Tooltip>
        )}
        {/* Narrow viewports get a close button instead of the admin
            download icon — there's no click-outside on a full-screen dialog. */}
        <Button
          variant="secondary"
          size="sm"
          className="tw-size-8 tw-p-0 tw-hidden -sm:tw-inline-flex"
          onClick={closeDialog}
          title="Close"
          aria-label="Close"
        >
          <IconX className="tw-text-base/none !tw-fill-none" />
        </Button>
      </div>
    </div>
  );
};
